// Окно фильтров в шапке таблицы «Модели ГПР» (владелец 2026-09-15): над заголовком
// «Работа», рядом с «Расчётом». Поиск работы с подсказками; выбранные работы и все
// фильтры верхней панели (корпуса, контуры, место, этаж, этап, подрядчик, группа работ) —
// тегами в этом же окне. Окно всегда в одну строку: лишние теги уходят вниз и скрыты,
// кнопка «▾ N» показывает все списком. Состояние — общий стор фильтров (src/filter.ts).
// Подсказки и список тегов рисуются в document.body (портал): шапка таблицы закреплена и
// создаёт свой слой — внутри неё выпадающий список прятался за заголовками.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { etazhiFiltra, filterStore, type FilterState } from './filter';

const DOP: [keyof FilterState, string][] = [
  ['mesto', 'Место'], ['etazh', 'Этаж'], ['etap', 'Этап'], ['podr', 'Подрядчик'], ['sistema', 'Группа работ'],
];

export function RabotyFiltr({ raboty }: { raboty: string[] }) {
  const [f, setF] = useState(filterStore.get());
  useEffect(() => filterStore.subscribe(() => setF(filterStore.get())), []);
  const [sug, setSug] = useState<string[]>([]);
  const [otkryt, setOtkryt] = useState(false);          // список всех тегов
  const [mesto, setMesto] = useState<React.CSSProperties>({});
  const box = useRef<HTMLDivElement>(null);
  const inp = useRef<HTMLInputElement>(null);

  // теги: сначала выбранные работы, потом фильтры верхней панели
  const tegi: { tekst: string; podskazka: string; snyat: () => void }[] = [];
  f.works.forEach(n => tegi.push({ tekst: n, podskazka: 'Работа: ' + n,
    snyat: () => filterStore.set({ works: f.works.filter(x => x !== n) }) }));
  f.korpusa.forEach(k => tegi.push({ tekst: 'Корпус: ' + k, podskazka: 'Корпус ' + k,
    snyat: () => filterStore.set({ korpusa: f.korpusa.filter(x => x !== k) }) }));
  f.kontury.forEach(k => tegi.push({ tekst: 'Контур: ' + k, podskazka: 'Контур ' + k,
    snyat: () => filterStore.set({ kontury: f.kontury.filter(x => x !== k) }) }));
  DOP.forEach(([k, lab]) => {
    const v = f[k] as string;
    if (k === 'etazh') {   // мультивыбор этажей — тег на каждый (#91)
      etazhiFiltra(v).forEach(e => tegi.push({ tekst: 'Этаж: ' + e, podskazka: 'Этаж ' + e,
        snyat: () => filterStore.set({ etazh: etazhiFiltra(v).filter(x => x !== e).join('|') }) }));
      return;
    }
    if (v) tegi.push({ tekst: lab + ': ' + v, podskazka: lab + ': ' + v,
      snyat: () => filterStore.set({ [k]: '' } as Partial<FilterState>) });
  });

  const podMesto = (shirina: number) => {
    const r = box.current?.getBoundingClientRect();
    if (r) setMesto({ position: 'fixed', left: r.left, top: r.bottom + 3, width: Math.max(r.width, shirina) });
  };
  const podobrat = (v: string, vybrany: string[] = f.works) => {
    const q = v.trim().toLowerCase();
    podMesto(320);
    setOtkryt(false);
    setSug(raboty.filter(n => !vybrany.includes(n) && (!q || n.toLowerCase().includes(q))).slice(0, 15));
  };
  const vybrat = (n: string) => {
    const works = [...f.works, n];
    filterStore.set({ works });
    // набранное остаётся, список тот же — можно выбрать «…1 сторона», потом «…2 сторона»
    podobrat(f.name, works);
  };

  // прокрутка — всплывающее закрывается; щелчок мимо окна и списка — тоже
  useEffect(() => {
    if (!sug.length && !otkryt) return;
    const scroll = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.rf-sloj')) return;
      // после выбора работы таблица перерисовывается и сама «прокручивается» — список
      // не закрываем (владелец 2026-09-15: «после выбора окно закрывается»), а двигаем
      // вслед за полем; закрываем, только когда поле ушло с экрана
      const r = box.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) { setSug([]); setOtkryt(false); return; }
      setMesto(m => ({ ...m, left: r.left, top: r.bottom + 3 }));
    };
    const mimo = (e: MouseEvent) => {
      if (e.target instanceof Element && (e.target.closest('.rf-sloj') || e.target.closest('.rf-box'))) return;
      setSug([]); setOtkryt(false);
    };
    window.addEventListener('scroll', scroll, true);
    document.addEventListener('mousedown', mimo);
    return () => { window.removeEventListener('scroll', scroll, true); document.removeEventListener('mousedown', mimo); };
  }, [sug.length, otkryt]);

  const teg = (t: typeof tegi[number], i: number) => (
    <span className="ftag" key={i} title={t.podskazka}>{t.tekst}
      <a title="Убрать этот фильтр"
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); t.snyat(); }}>×</a>
    </span>
  );

  return (
    <div className="rf-box" ref={box}
      title="Фильтры модели: начните вводить название работы и выберите из списка; здесь же — все выбранные фильтры"
      onMouseDown={e => { if (e.target === e.currentTarget) { e.preventDefault(); inp.current?.focus(); } }}>
      <input ref={inp} value={f.name} autoComplete="off" placeholder="Работа…"
        onChange={e => { filterStore.set({ name: e.target.value }); podobrat(e.target.value); }}
        onFocus={() => podobrat(f.name)}
        onBlur={() => window.setTimeout(() => setSug([]), 150)}
        onKeyDown={e => {
          if (e.key === 'Enter' && sug.length) { e.preventDefault(); vybrat(sug[0]); }
          else if (e.key === 'Escape') { setSug([]); setOtkryt(false); }
          else if (e.key === 'Backspace' && !f.name && f.works.length) {
            filterStore.set({ works: f.works.slice(0, -1) });
          }
        }} />
      {/* теги в одну строку: перенос уходит вниз и скрыт, окно не растёт */}
      <span className="rf-tegi">{tegi.map(teg)}</span>
      {tegi.length > 0 && (
        <button type="button" className={'rf-more' + (otkryt ? ' on' : '')}
          title={otkryt ? 'Свернуть список фильтров' : 'Показать все выбранные фильтры списком'}
          onMouseDown={e => { e.preventDefault(); e.stopPropagation();
            if (!otkryt) { podMesto(360); setSug([]); } setOtkryt(!otkryt); }}>
          {otkryt ? '▴' : '▾ ' + tegi.length}</button>
      )}
      {(tegi.length > 0 || f.name) && (
        <a className="rf-x" title="Снять все фильтры"
          onMouseDown={e => { e.preventDefault(); e.stopPropagation();
            filterStore.reset(); setSug([]); setOtkryt(false); }}>✕</a>
      )}
      {sug.length > 0 && createPortal(
        <div className="wsug rf-sloj rf-sug" style={{ ...mesto, display: 'block' }}>
          {sug.map((n, i) => (
            <a key={n} className={i === 0 ? 'hot' : undefined}
              onMouseDown={e => { e.preventDefault(); vybrat(n); }}>{n}</a>
          ))}
        </div>, document.body)}
      {otkryt && tegi.length > 0 && createPortal(
        <div className="rf-sloj rf-spisok" style={mesto}>
          <div className="rf-spisok-tegi">{tegi.map(teg)}</div>
          <a className="rf-spisok-snyat"
            onMouseDown={e => { e.preventDefault(); filterStore.reset(); setOtkryt(false); }}>Снять все фильтры</a>
        </div>, document.body)}
    </div>
  );
}
